/**
 * /admin/marketing/lifecycle-audit — Session 88 pt11 (C)
 *
 * Bulk audit view of why leads are paused or inactive across the
 * whole tenant. Useful for spotting patterns ("30 leads paused in
 * March — what happened then?") without clicking into each one.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppLayout from "@/components/layout/AppLayout";
import { toast } from "sonner";

const REASON_LABELS = {
  invalid_email:               "Email bounced (auto)",
  invalid_email_inferred:      "Email bounced (inferred)",
  unsubscribed:                "Unsubscribed (auto)",
  unsubscribed_inferred:       "Unsubscribed (inferred)",
  booking_click:               "Booked meeting (auto)",
  booking_click_inferred:      "Booked meeting (inferred)",
  engagement_reply:            "Replied to drip (auto)",
  do_not_contact_inferred:     "Do not contact (manual)",
  orphan_cleanup:              "Orphan cleanup",
  historical_unknown_inferred: "Pre-tracking — unknown",
  "(unrecorded)":              "Not recorded",
};

const MONTH = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function LifecycleAuditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/marketing/lifecycle-audit")
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AppLayout><div className="p-6 text-neutral-500">Loading…</div></AppLayout>
  );

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto p-6 space-y-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">
            Admin · Marketing
          </div>
          <h1 className="text-3xl font-semibold mt-1 text-neutral-900">
            Lead lifecycle audit
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            Bulk view of why leads are paused or inactive across the tenant.
            Sourced live from <code>crm_leads.pause_reason</code> /{" "}
            <code>lead_status</code> / <code>last_action_date</code>. Inferred
            values come from the historical backfill where the original
            reason wasn&apos;t recorded.
          </p>
        </div>

        <Section title="Why leads are paused"
                 testid="audit-paused-table"
                 rows={data?.paused_by_reason || []}
                 keyCol="reason"
                 labelMap={REASON_LABELS}
                 emptyText="No paused leads." />

        <Section title="Status of inactive leads"
                 testid="audit-inactive-table"
                 rows={data?.inactive_by_status || []}
                 keyCol="status"
                 emptyText="No inactive leads." />

        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-700 mb-3">
            Pauses by month (last 12)
          </h2>
          {(data?.paused_by_month || []).length === 0 ? (
            <div className="text-sm text-neutral-400 italic">No data.</div>
          ) : (
            <table className="w-full text-sm" data-testid="audit-monthly-table">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-neutral-500">
                    Month
                  </th>
                  <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-neutral-500">
                    Leads paused
                  </th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.paused_by_month.map((r, i) => {
                  const max = Math.max(...data.paused_by_month.map(x => x.count));
                  const pct = max > 0 ? (r.count / max) * 100 : 0;
                  return (
                    <tr key={i} className="border-t border-neutral-100">
                      <td className="px-3 py-2 text-neutral-700">
                        {MONTH[(r.month || 1) - 1]} {r.year}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-neutral-900">
                        {r.count.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 w-1/2">
                        <div className="h-2 bg-neutral-100 rounded">
                          <div
                            className="h-2 bg-amber-400 rounded"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
}


function Section({ title, rows, keyCol, labelMap, emptyText, testid }) {
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <div>
      <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-700 mb-3">
        {title} · {total.toLocaleString()} total
      </h2>
      {rows.length === 0 ? (
        <div className="text-sm text-neutral-400 italic">{emptyText}</div>
      ) : (
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-neutral-50">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-neutral-500">
                {keyCol === "reason" ? "Reason" : "Status"}
              </th>
              <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-neutral-500">
                Leads
              </th>
              <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-neutral-500">
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const label = labelMap?.[r[keyCol]] ?? r[keyCol];
              const pct = total > 0 ? (r.count / total) * 100 : 0;
              return (
                <tr key={i} className="border-t border-neutral-100">
                  <td className="px-3 py-2 text-neutral-700">{label}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-neutral-900">
                    {r.count.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right text-neutral-500 text-xs">
                    {pct.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
